import React from 'react';
import { Link } from 'react-router-dom';
import styled, { css } from 'styled-components';

// Base button styles
const baseButtonStyles = css`
  display: inline-block;
  padding: 12px 25px;
  border-radius: 4px;
  font-weight: 600;
  text-align: center;
  cursor: pointer;
  transition: ${props => props.theme.transitions.main};
  border: none;
  font-size: ${props => props.size === 'large' 
    ? props.theme.fontSizes.medium 
    : props.size === 'small' 
      ? props.theme.fontSizes.small 
      : props.theme.fontSizes.regular};
  
  &:disabled {
    opacity: 0.7;
    cursor: not-allowed;
  }
`;

// Button variants
const ButtonVariant = css`
  ${props => {
    if (props.variant === 'primary') {
      return css`
        background-color: ${props.theme.colors.primaryColor};
        color: white;
        
        &:hover:not(:disabled) {
          background-color: #2980b9;
        }
      `;
    } else if (props.variant === 'secondary') {
      return css`
        background-color: ${props.theme.colors.secondaryColor};
        color: white;
        
        &:hover:not(:disabled) {
          background-color: #1a252f;
        }
      `;
    } else if (props.variant === 'outline') {
      return css`
        background-color: transparent;
        border: 2px solid ${props.outline === 'primary' 
          ? props.theme.colors.primaryColor 
          : props.theme.colors.secondaryColor};
        color: ${props.outline === 'primary' 
          ? props.theme.colors.primaryColor 
          : props.theme.colors.secondaryColor};
        
        &:hover:not(:disabled) {
          background-color: ${props.outline === 'primary' 
            ? props.theme.colors.primaryColor 
            : props.theme.colors.secondaryColor};
          color: white;
        }
      `;
    } else if (props.variant === 'text') {
      return css`
        background-color: transparent;
        color: ${props.theme.colors.primaryColor};
        padding: 8px 16px;
        
        &:hover:not(:disabled) {
          background-color: rgba(52, 152, 219, 0.05);
        }
      `;
    }
  }}
`;

// Full width styles
const fullWidthStyles = css`
  display: block;
  width: 100%;
`;

// StyledButton component
const StyledButton = styled.button`
  ${baseButtonStyles}
  ${ButtonVariant}
  ${props => props.fullWidth && fullWidthStyles}
`;

// StyledLink component for Link buttons
const StyledLink = styled(Link)`
  ${baseButtonStyles}
  ${ButtonVariant}
  ${props => props.fullWidth && fullWidthStyles}
`;

// Main Button component
const Button = ({ 
  children, 
  variant = 'primary', 
  size = 'medium', 
  outline = 'primary',
  fullWidth = false,
  to = null, 
  as = null,
  onClick = null,
  type = 'button',
  disabled = false,
  className = '',
  ...rest 
}) => {
  // If 'to' prop is provided, render as Link
  if (to) {
    return (
      <StyledLink
        to={to}
        variant={variant}
        size={size}
        outline={outline}
        fullWidth={fullWidth}
        className={className}
        as={as}
        {...rest}
      >
        {children}
      </StyledLink>
    );
  }
  
  // Otherwise render as button
  return (
    <StyledButton
      type={type}
      variant={variant}
      size={size}
      outline={outline}
      fullWidth={fullWidth}
      onClick={onClick}
      disabled={disabled}
      className={className}
      as={as}
      {...rest}
    >
      {children}
    </StyledButton>
  );
};

export default Button;
